// client/src/features/quotations/CatalogueVatPrompt.tsx
//
// "DOES THIS ITEM HAVE VAT?" — a small popup, not a form.
//
// Owner decision (2026-09-24, "one Trinidad VAT rate"): a Master Catalogue
// row's VAT is its boolean `vat_applicable`. A BLANK row (nobody has ever
// said yes or no) is priced without VAT until somebody answers this — never
// guessed, and never left silently under-costed either. This popup is the
// ONE place that answer is given, from two entry points on
// `QuotationDetailPage`:
//   - right after a catalogue pick, when the picked row's `vat_applicable`
//     is blank (item-lookup panel catalogue pick, or a supplier-dropdown pick
//     of a Master Catalogue option);
//   - the small "VAT?" badge on a GENERATED line whose
//     `pricingDetails.catalogueVatUnset === true` — the row was blank when the
//     quotation was generated and nobody was there to ask.
//
// This component is deliberately dumb: it shows the question and the
// Settings rate, and hands `onYes` / `onNo` / `onCancel` back to the page.
// The page is the one that calls `masterCatalogueApi.setCatalogueVat` and
// re-prices the line (`QuotationDetailPage`'s own doc comment on its
// `vatPrompt` state has the two re-pricing paths and why they differ) — this
// file writes no pricing math of its own, per CLAUDE.md's one-owner rule.

import React from 'react';
import { Modal } from '../../components/Modal';

export interface CatalogueVatPromptProps {
  isOpen: boolean;
  /** The catalogue row's own name — never the vessel's own wording for the line. */
  itemName: string;
  /** The Settings VAT rate (`getVatRate`), percent, or null while it has not loaded / has never been set. */
  vatRatePercent: number | null;
  /** True while an answer is being saved — Yes, No and Cancel are disabled. */
  saving?: boolean;
  onYes: () => void;
  onNo: () => void;
  /** Omit to make the question unskippable (D101.1): no Cancel, no X, Escape and backdrop do nothing. */
  onCancel?: () => void;
}

/**
 * "VAT 12.5% (from Settings)", or a rate-neutral refusal to guess while the
 * rate has not loaded. With no rate the server can save a "No" but refuses a
 * "Yes" (VAT_RATE_NOT_SET): a Yes re-prices the row's stored costs, and there
 * is no rate to price them at. The popup says so rather than promising a save.
 */
function vatRateLine(vatRatePercent: number | null): string {
  return typeof vatRatePercent === 'number' && Number.isFinite(vatRatePercent)
    ? `VAT ${vatRatePercent}% (from Settings)`
    : 'The VAT rate is not set in Settings — a Yes cannot be saved or costed until an admin sets it; a No is saved.';
}

export const CatalogueVatPrompt: React.FC<CatalogueVatPromptProps> = ({
  isOpen,
  itemName,
  vatRatePercent,
  saving = false,
  onYes,
  onNo,
  onCancel,
}) => {
  return (
    <Modal isOpen={isOpen} title="Does this item have VAT?" onClose={onCancel} maxWidthClass="max-w-sm" overlayZClass="z-[80]">
      <div className="space-y-3 font-['Public_Sans']">
        <p className="text-sm text-slate-900">
          Does <span className="font-semibold">{itemName || 'this item'}</span> have VAT?
        </p>
        <p className="text-xs text-slate-500">{vatRateLine(vatRatePercent)}</p>
        <div className="flex items-center justify-end gap-2 pt-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="px-3 py-1.5 text-xs font-medium text-slate-600 rounded-md border border-[#D7DBD9] hover:bg-[#F9FAF9] disabled:opacity-50"
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={onNo}
            disabled={saving}
            className="px-3 py-1.5 text-xs font-semibold text-slate-900 rounded-md border border-[#D7DBD9] hover:bg-[#F9FAF9] disabled:opacity-50"
          >
            No
          </button>
          <button
            type="button"
            onClick={onYes}
            disabled={saving}
            className="px-3 py-1.5 text-xs font-semibold text-white rounded-md bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
          >
            Yes
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CatalogueVatPrompt;
